// What's new: a dated list of shipped changes, written from ticket closing
// notes plus entries staff add directly. Staff can hide and reword items, and
// review the Friday facilitator email here before a super admin sends it.
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { whatsNewAPI } from '../api/client';
import WhatsNewDraftModal from '../components/WhatsNewDraftModal';
import type { WhatsNewDraft, WhatsNewFeed, WhatsNewItem } from '../types';

function dayLabel(value: string) {
  return new Date(value).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function dayKey(value: string) {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function errorText(err: unknown, fallback: string) {
  return (err as { response?: { data?: { error?: string } } }).response?.data?.error || fallback;
}

export default function WhatsNew() {
  const [feed, setFeed] = useState<WhatsNewFeed | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState<WhatsNewDraft | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(() => {
    whatsNewAPI.list()
      .then(setFeed)
      .catch(() => {
        setFeed({ items: [], can_edit: false });
        toast.error("Could not load What's new.");
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Review links from the email and Telegram: /whats-new?digest=12
  const linkedDigest = searchParams.get('digest');
  useEffect(() => {
    if (!linkedDigest || !feed) return;
    if (feed.draft && String(feed.draft.id) === linkedDigest) setReviewing(feed.draft);
    else if (feed.can_edit) toast('That email was already sent or discarded.');
    setSearchParams((params) => {
      params.delete('digest');
      return params;
    }, { replace: true });
  }, [linkedDigest, feed, setSearchParams]);

  const startEdit = (key: string, item?: WhatsNewItem) => {
    setEditing(key);
    setTitle(item?.title || '');
    setBody(item?.body || '');
  };

  const save = async (item?: WhatsNewItem) => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      if (!item) await whatsNewAPI.addEntry(title.trim(), body.trim());
      else if (item.source === 'entry' && item.entry_id) await whatsNewAPI.editEntry(item.entry_id, title.trim(), body.trim());
      else if (item.ticket_id) await whatsNewAPI.updateTicket(item.ticket_id, { title: title.trim(), note: body.trim() });
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not save that.'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (action: () => Promise<unknown>, fail: string) => {
    setBusy(true);
    try {
      await action();
      load();
    } catch (err) {
      toast.error(errorText(err, fail));
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const result = await whatsNewAPI.buildDraft();
      if (result.draft) setReviewing(result.draft);
      else toast(result.reason || 'Nothing new to send.');
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not build the email.'));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (draft: WhatsNewDraft, approve: boolean) => {
    setBusy(true);
    try {
      if (approve) {
        const result = await whatsNewAPI.approveDigest(draft.id);
        toast.success(`Sent to ${result.digest.recipient_count} ${result.digest.recipient_count === 1 ? 'person' : 'people'}.`);
      } else {
        await whatsNewAPI.discardDigest(draft.id);
        toast('Discarded. Nothing was sent.');
      }
      setReviewing(null);
      load();
    } catch (err) {
      toast.error(errorText(err, approve ? 'Could not send the email.' : 'Could not discard the email.'));
      load();
    } finally {
      setBusy(false);
    }
  };

  const canEdit = Boolean(feed?.can_edit);
  const items = feed?.items || [];
  const groups: { key: string; label: string; items: WhatsNewItem[] }[] = [];
  items.forEach((item) => {
    const key = dayKey(item.date);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(item);
    else groups.push({ key, label: dayLabel(item.date), items: [item] });
  });

  const editor = (item?: WhatsNewItem) => (
    <div className="space-y-2">
      <input
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        maxLength={140}
        placeholder="What changed, in a few words"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
      />
      <textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        rows={3}
        placeholder={item?.source === 'ticket' ? 'Leave empty to use the closing note' : 'A sentence or two for members'}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:outline-none"
      />
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy || !title.trim()}
          onClick={() => void save(item)}
          className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => setEditing(null)}
          className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 transition"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
          <Sparkles size={22} className="text-gold" />
          What's new
        </h1>
        <p className="text-gray-600 mt-1 text-sm">
          Changes to the app, newest first. Updates ship on Fridays. Something missing?{' '}
          <Link to="/requests" className="text-navy underline">See your requests</Link>.
        </p>
      </div>

      {canEdit && (
        <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-600 min-w-0">
              {feed?.last_digest
                ? `Friday email last sent ${new Date(feed.last_digest.approved_at || feed.last_digest.created_at).toLocaleDateString()} to ${feed.last_digest.recipient_count} people. ${feed.unsent_count ?? 0} new since.`
                : 'The Friday email has not gone out yet.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => startEdit('new')}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition disabled:opacity-50"
              >
                <Plus size={15} />
                Add entry
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void sendNow()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition disabled:opacity-50"
              >
                <Mail size={15} />
                Send Friday email now
              </button>
            </div>
          </div>
          {feed?.draft && (
            <div className="rounded-xl border border-gold/50 bg-gold/10 p-3 flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-navy">Friday email waiting for approval</p>
                <p className="text-xs text-gray-600">
                  {feed.draft.subject} · {feed.draft.entry_count} update{feed.draft.entry_count === 1 ? '' : 's'} · to{' '}
                  {feed.draft.recipient_count} {feed.draft.recipient_count === 1 ? 'person' : 'people'}
                  {feed.draft.sender_only ? ' (staging: only you)' : ''}
                </p>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() => setReviewing(feed.draft ?? null)}
                className="px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition disabled:opacity-50"
              >
                Review
              </button>
            </div>
          )}
          {editing === 'new' && editor()}
        </div>
      )}

      {feed === null && <p className="text-sm text-gray-400">Loading…</p>}
      {feed && items.length === 0 && (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-500 shadow-sm">
          Nothing here yet. Shipped changes show up after each Friday release.
        </div>
      )}

      {groups.map((group) => (
        <section key={group.key} className="space-y-2">
          <h2 className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{group.label}</h2>
          {group.items.map((item) => (
            <article
              key={item.id}
              className={`bg-white rounded-2xl p-4 shadow-sm ${item.hidden ? 'opacity-60' : ''}`}
            >
              {editing === item.id ? editor(item) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="min-w-0 font-semibold text-navy text-sm">{item.title}</h3>
                    {item.hidden && (
                      <span className="shrink-0 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Hidden</span>
                    )}
                  </div>
                  {item.body && <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{item.body}</p>}
                  {canEdit && (
                    <div className="mt-3 flex flex-wrap gap-3 text-xs">
                      <button
                        type="button"
                        onClick={() => startEdit(item.id, item)}
                        className="flex items-center gap-1 text-gray-500 hover:text-navy transition"
                      >
                        <Pencil size={13} />
                        Edit
                      </button>
                      {item.source === 'ticket' && item.ticket_id && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void act(
                            () => whatsNewAPI.updateTicket(item.ticket_id as number, { hidden: !item.hidden }),
                            'Could not update that.',
                          )}
                          className="flex items-center gap-1 text-gray-500 hover:text-navy transition"
                        >
                          {item.hidden ? <Eye size={13} /> : <EyeOff size={13} />}
                          {item.hidden ? 'Show' : 'Hide'}
                        </button>
                      )}
                      {item.source === 'ticket' && item.ticket_id && (
                        <Link to={`/admin/tickets?ticket=${item.ticket_id}`} className="text-gray-500 hover:text-navy transition">
                          Open ticket
                        </Link>
                      )}
                      {item.source === 'entry' && item.entry_id && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void act(
                            () => whatsNewAPI.deleteEntry(item.entry_id as number),
                            'Could not delete that.',
                          )}
                          className="flex items-center gap-1 text-gray-500 hover:text-red-600 transition"
                        >
                          <Trash2 size={13} />
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </article>
          ))}
        </section>
      ))}

      {reviewing && (
        <WhatsNewDraftModal
          draft={reviewing}
          busy={busy}
          onApprove={() => void decide(reviewing, true)}
          onDiscard={() => void decide(reviewing, false)}
          onClose={() => setReviewing(null)}
        />
      )}
    </div>
  );
}
